import React, { FC, Fragment } from 'react';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { InfoBubble } from '../InfoBubble';
import {
  formatPrizeAmount,
  formatPrizeDate,
  truncatePrizeMessage,
  usePrizesCardController,
} from './usePrizesCardController';
import './Prizes.scss';

export interface PrizesCardProps {
  cohortUserId: number;
}

export const PRIZES_SLOTH_SRC = '/assets/svg/prizes-sloth.svg';

export const PrizesCard: FC<PrizesCardProps> = ({ cohortUserId }) => {
  const { prizes, hasPrizes, selectedPrize, openPrize, closePrize } = usePrizesCardController(cohortUserId);

  // Nothing to celebrate yet - the card stays off the dashboard entirely, wrapper included,
  // so it doesn't leave an empty drop shadow behind
  if (!hasPrizes) {
    return null;
  }

  return (
    <Fragment>
      <div className="prizesCardJoyride dashboard-card">
        <Card
          variant={'elevation'}
          sx={{
            border: '1px solid',
            borderRadius: 4,
          }}
        >
          <CardContent>
            <div
              className="title-with-info"
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 6, // space between title and info icon
                flexWrap: 'nowrap',
              }}
            >
              <Typography variant="h5" component="div">
                Prizes
              </Typography>
              <InfoBubble title="Prizes">
                Every prize and gift card you&#39;ve earned shows up here. Tap one to see the full message, and use the
                claim link if you haven&#39;t redeemed it yet.
              </InfoBubble>
            </div>
            <div className="card-content-wrapper">
              <img src={PRIZES_SLOTH_SRC} alt="Prizes" className="card-logo" />
              <List className="prizes-list">
                {prizes.map((prize, index) => (
                  <Fragment key={prize.id}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem
                      disablePadding
                      secondaryAction={
                        prize.link ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            href={prize.link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Claim
                          </Button>
                        ) : undefined
                      }
                    >
                      <ListItemButton
                        onClick={() => openPrize(prize)}
                        aria-label={`View prize details from ${formatPrizeDate(prize.receivedOn)}`}
                      >
                        <ListItemText
                          primary={
                            <>
                              <Typography variant="cursive" component="span" className="prize-amount">
                                {formatPrizeAmount(prize.amount) || 'Prize'}
                              </Typography>
                              <Typography variant="body2" component="span" className="prize-message">
                                {truncatePrizeMessage(prize.message) || 'Tap for details'}
                              </Typography>
                            </>
                          }
                          secondary={<span className="prize-date">{formatPrizeDate(prize.receivedOn)}</span>}
                        />
                      </ListItemButton>
                    </ListItem>
                  </Fragment>
                ))}
              </List>
            </div>
          </CardContent>
        </Card>
      </div>
      <Dialog open={Boolean(selectedPrize)} onClose={closePrize} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedPrize ? formatPrizeAmount(selectedPrize.amount) || 'Your Prize' : ''}</DialogTitle>
        <DialogContent>
          <Stack direction={'column'} sx={{ gap: 2 }}>
            {selectedPrize?.receivedOn && (
              <Typography variant="body2" color="text.secondary">
                {formatPrizeDate(selectedPrize.receivedOn)}
              </Typography>
            )}
            {selectedPrize?.message && <Typography component="p">{selectedPrize.message}</Typography>}
            {selectedPrize?.link && (
              <Button
                variant="contained"
                color="primary"
                href={selectedPrize.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: 'flex-start' }}
              >
                Claim Your Prize
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant={'outlined'} onClick={closePrize}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};
